import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { AlertTriangle, ArrowLeft, Home, SearchX, ShieldAlert, WifiOff } from 'lucide-react';
import logo from 'assets/images/image.png';

const statusContent = {
    authentication: {
        eyebrow: 'Secure sign-in',
        title: 'We couldn’t complete sign-in',
        description: 'Your secure sign-in could not be started. Please try again or return to the sign-in page.',
        icon: ShieldAlert
    },
    forbidden: {
        eyebrow: 'Access restricted',
        title: 'You don’t have access to this page',
        description: 'Your account is signed in, but it does not have permission to open this area.',
        icon: ShieldAlert
    },
    loading: {
        eyebrow: 'Secure sign-in',
        title: 'Connecting you securely',
        description: 'We’re preparing the Travel Management System and checking your sign-in session.',
        icon: null
    },
    network: {
        eyebrow: 'Service connection',
        title: 'The service is temporarily unavailable',
        description: 'We couldn’t connect to the Travel Management System. Check your connection, then try again.',
        icon: WifiOff
    },
    notFound: {
        eyebrow: 'Page not found',
        title: 'That page isn’t here',
        description: 'The address may be incorrect, or the page may have moved. Return to the dashboard to continue.',
        icon: SearchX
    },
    unexpected: {
        eyebrow: 'Something went wrong',
        title: 'We couldn’t load this page',
        description: 'An unexpected problem interrupted your request. Please try again in a moment.',
        icon: AlertTriangle
    }
};

const ErrorPage = ({
    status = 'network',
    title,
    description,
    onRetry,
    primaryActionLabel,
    primaryActionPath,
    onSecondaryAction,
    secondaryActionLabel,
    secondaryActionPath,
    supportText = 'Need help? Contact your ZIM-TTECH IT support team and mention the time this happened.'
}) => {
    const content = statusContent[status];
    const StatusIcon = content.icon;
    const isLoading = status === 'loading';

    return (
        <Box
            component="main"
            sx={{
                alignItems: 'center',
                bgcolor: 'zimttech.canvas',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '100vh',
                overflow: 'hidden',
                px: { xs: 3, sm: 4 },
                py: { xs: 3, sm: 5 },
                '@supports (height: 100dvh)': {
                    minHeight: '100dvh'
                }
            }}
        >
            <Box
                sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'zimttech.border',
                    borderRadius: '12px',
                    boxShadow: '0 18px 48px rgba(25, 38, 74, 0.1)',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 0.82fr) minmax(420px, 1.18fr)' },
                    maxWidth: 900,
                    overflow: 'hidden',
                    width: '100%'
                }}
            >
                <Box
                    component="aside"
                    aria-label="ZIM-TTECH Travel Management System"
                    sx={{
                        bgcolor: 'zimttech.brand',
                        color: 'common.white',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        minHeight: { xs: 'auto', md: 440 },
                        overflow: 'hidden',
                        p: { xs: 3, sm: 4, md: 5 },
                        position: 'relative'
                    }}
                >
                    <Box
                        sx={{
                            alignItems: { xs: 'center', md: 'flex-start' },
                            display: 'flex',
                            flexDirection: { xs: 'row', md: 'column' },
                            gap: { xs: 2, md: 3 },
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        <Box
                            sx={{
                                bgcolor: 'common.white',
                                borderRadius: '10px',
                                display: 'inline-flex',
                                p: { xs: 1, md: 1.25 }
                            }}
                        >
                            <Box
                                component="img"
                                src={logo}
                                alt="ZIM-TTECH"
                                sx={{ display: 'block', height: 'auto', width: { xs: 128, md: 184 } }}
                            />
                        </Box>
                        <Box>
                            <Typography
                                component="p"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.72)',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase'
                                }}
                            >
                                Travel Management System
                            </Typography>
                            <Typography component="p" sx={{ fontSize: { xs: '1rem', md: '1.35rem' }, fontWeight: 600, mt: 0.75 }}>
                                Travel, simplified.
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        aria-hidden="true"
                        sx={{
                            bottom: -22,
                            display: { xs: 'none', md: 'grid' },
                            gap: 1,
                            gridTemplateColumns: 'repeat(2, 54px)',
                            opacity: 0.58,
                            position: 'absolute',
                            right: -12,
                            transform: 'rotate(-4deg)'
                        }}
                    >
                        <Box sx={{ bgcolor: 'zimttech.highlight', height: 54 }} />
                        <Box sx={{ bgcolor: 'zimttech.accent', height: 54 }} />
                        <Box sx={{ bgcolor: 'zimttech.accent', height: 54 }} />
                        <Box sx={{ bgcolor: 'zimttech.highlight', height: 54 }} />
                    </Box>
                </Box>

                <Box
                    component="section"
                    aria-live={isLoading ? 'polite' : 'assertive'}
                    role={isLoading ? 'status' : 'alert'}
                    sx={{ alignSelf: 'center', p: { xs: 3, sm: 5, md: 6 } }}
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            bgcolor: isLoading ? 'zimttech.brandSoft' : 'zimttech.errorSoft',
                            borderRadius: '12px',
                            color: isLoading ? 'zimttech.brand' : 'error.dark',
                            display: 'flex',
                            height: 52,
                            justifyContent: 'center',
                            mb: 3,
                            width: 52
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress aria-label="Connecting securely" color="inherit" size={26} thickness={4} />
                        ) : (
                            <StatusIcon size={26} />
                        )}
                    </Box>

                    <Typography
                        component="p"
                        sx={{
                            color: 'zimttech.accent',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            letterSpacing: '0.09em',
                            textTransform: 'uppercase'
                        }}
                    >
                        {content.eyebrow}
                    </Typography>
                    <Typography
                        component="h1"
                        sx={{ color: 'zimttech.ink', fontSize: { xs: '1.65rem', sm: '2rem' }, fontWeight: 700, lineHeight: 1.18, mt: 1 }}
                    >
                        {title || content.title}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '1rem', lineHeight: 1.65, mt: 2, maxWidth: 510 }}>
                        {description || content.description}
                    </Typography>

                    {!isLoading && (onRetry || primaryActionPath || onSecondaryAction || secondaryActionPath) && (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                            {primaryActionPath ? (
                                <Button component={Link} to={primaryActionPath} variant="contained" startIcon={<Home size={18} />}>
                                    {primaryActionLabel || 'Return to dashboard'}
                                </Button>
                            ) : (
                                onRetry && (
                                    <Button onClick={onRetry} variant="contained">
                                        {primaryActionLabel || 'Try again'}
                                    </Button>
                                )
                            )}
                            {secondaryActionPath ? (
                                <Button component={Link} to={secondaryActionPath} variant="outlined" startIcon={<ArrowLeft size={18} />}>
                                    {secondaryActionLabel || 'Back to home'}
                                </Button>
                            ) : (
                                onSecondaryAction && (
                                    <Button onClick={onSecondaryAction} variant="outlined" startIcon={<ArrowLeft size={18} />}>
                                        {secondaryActionLabel || 'Return to sign in'}
                                    </Button>
                                )
                            )}
                        </Stack>
                    )}

                    {!isLoading && supportText && (
                        <Typography
                            sx={{
                                borderLeft: '3px solid',
                                borderColor: 'zimttech.highlight',
                                color: 'text.secondary',
                                fontSize: '0.8125rem',
                                lineHeight: 1.6,
                                mt: 4,
                                pl: 2
                            }}
                        >
                            {supportText}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

ErrorPage.propTypes = {
    description: PropTypes.string,
    onRetry: PropTypes.func,
    onSecondaryAction: PropTypes.func,
    primaryActionLabel: PropTypes.string,
    primaryActionPath: PropTypes.string,
    secondaryActionLabel: PropTypes.string,
    secondaryActionPath: PropTypes.string,
    status: PropTypes.oneOf(['authentication', 'forbidden', 'loading', 'network', 'notFound', 'unexpected']),
    supportText: PropTypes.string,
    title: PropTypes.string
};

export default ErrorPage;
